import { useAtom } from '@reatom/npm-react'
import { memo } from 'react'

import { shareTone, signedTone } from '../../model/pulse-readings'
import { percent, shortAddress } from '../portfolio-screen/money'

import type { Tone } from '../../model/pulse-readings'
import type { PairModel } from '@root/entities/pair'

const toneVar = (tone: Tone) =>
  ({
    quiet: 'var(--t2)',
    watch: 'var(--warn)',
    loud: 'var(--down)',
    good: 'var(--up)',
    bad: 'var(--down)',
  })[tone]

/**
 * Who is holding this, and how much of it they have between them.
 *
 * The same six readings the old panel showed, on the same ladder the board's
 * rows use — under a tenth is quiet, a fifth is worth a look, more than that
 * is the thing to know before buying. They are together in one block because
 * they answer one question, and apart they read as trivia.
 */
export const RiskPanel: React.FC<{ pair: PairModel }> = memo(({ pair }) => {
  const [holders] = useAtom(pair.holdersCount)
  const [top10] = useAtom(pair.top10holding)
  const [audit] = useAtom(pair.audit)
  const [snipers] = useAtom(pair.snipers)
  const [insiders] = useAtom(pair.insiders)
  const [bundlers] = useAtom(pair.bundlers)
  const [snipersMeta] = useAtom(pair.snipersMeta)
  const [holdersChange] = useAtom(pair.stats['1H'].holdersChange)

  const dev = Number(audit.checks.ownerTokenBalance.percent)

  return (
    <section className="pn">
      <header>
        <h2>Who holds it</h2>
        <div className="right">
          <span className="tag">
            {Number(holders).toLocaleString()} holders
          </span>
        </div>
      </header>

      <div className="in">
        <div className="risk">
          <Cell label="Top 10" value={Number(top10)} />
          <Cell label="Dev" value={dev} />
          <Cell label="Snipers" value={Number(snipers.holding)} />
          <Cell label="Insiders" value={Number(insiders.holding)} />
          <Cell label="Bundlers" value={Number(bundlers.holding)} />
          <Cell
            label="Together"
            value={
              Number(snipers.holding) +
              Number(insiders.holding) +
              Number(bundlers.holding)
            }
          />
          {/*
            Two readings that are not shares of supply and so do not take the
            same colour ladder: whether the pool can still be pulled, and
            whether anybody is still arriving.
          */}
          <div className="rc">
            <i>LP burned</i>
            <b
              className="mono"
              style={{ color: lpTone(audit.checks.lpBurned.percent) }}
            >
              {lpWord(audit.checks.lpBurned.percent)}
            </b>
          </div>
          <div className="rc">
            <i>Holders 1h</i>
            <b
              className="mono"
              style={{ color: toneVar(signedTone(Number(holdersChange))) }}
            >
              {percent(Number(holdersChange), { sign: true })}
            </b>
          </div>
        </div>

        {/*
          A different figure from the one above it, and the old page showed
          both without saying so: this is how many of the first buyers were
          sniping, not what share of supply they hold.
        */}
        {snipersMeta ? (
          <p className="hint">
            <b style={{ color: 'var(--t2)' }}>
              {snipersMeta.count} of the first {snipersMeta.total}
            </b>{' '}
            buyers were snipers.
          </p>
        ) : undefined}

        {audit.meta.deployerAddress ? (
          <div className="row">
            <span className="lb">Deployer</span>
            <button
              className="addr mono"
              type="button"
              onClick={() =>
                navigator.clipboard
                  .writeText(String(audit.meta.deployerAddress))
                  .catch(() => undefined)
              }
            >
              {shortAddress(String(audit.meta.deployerAddress), 5)}
            </button>
          </div>
        ) : undefined}
      </div>
    </section>
  )
})

/** A burned pool cannot be pulled; a live one can, whatever its size. */
const lpWord = (percent: unknown): string => {
  const burned = Number(percent)
  if (!Number.isFinite(burned)) {
    return '—'
  }
  if (burned >= 100) {
    return 'Burned'
  }
  return burned > 0 ? `${burned.toFixed(0)}%` : 'No'
}

const lpTone = (percent: unknown): string => {
  const burned = Number(percent)
  if (!Number.isFinite(burned)) {
    return 'var(--t4)'
  }
  return burned >= 100 ? 'var(--up)' : 'var(--down)'
}

const Cell: React.FC<{ label: string; value: number }> = memo(
  ({ label, value }) => {
    const known = Number.isFinite(value)

    return (
      <div className="rc">
        <i>{label}</i>
        <b
          className="mono"
          style={{ color: known ? toneVar(shareTone(value)) : 'var(--t4)' }}
        >
          {known ? percent(value) : '—'}
        </b>
      </div>
    )
  },
)
